import { useCallback } from 'react';
import { InfiniteComboBox, type ComboBoxSection } from './InfiniteComboBox';
import { tenantFilamentApi, filamentLibraryApi } from '@/features/filaments/api';

interface FilamentSelectProps {
  value: string;
  onChange: (value: string, isFromLibrary: boolean) => void;
  disabled?: boolean;
  error?: string;
  label?: string;
}

export function FilamentSelect({
  value,
  onChange,
  disabled,
  error,
  label = 'Filament',
}: FilamentSelectProps) {
  const fetchTenantFilaments = useCallback(
    async ({ page, search }: { page: number; search: string }) => {
      const result = await tenantFilamentApi.list({
        page,
        limit: 50,
        search: search || undefined,
      });

      return {
        items: result.items.map((tf) => ({
          id: tf.id,
          label: `${tf.filament.manufacturer?.name} - ${tf.filament.name} (${tf.filament.colorName})`,
        })),
        hasMore: result.pagination.page < result.pagination.totalPages,
      };
    },
    []
  );

  const fetchLibraryFilaments = useCallback(
    async ({ page, search }: { page: number; search: string }) => {
      const result = await filamentLibraryApi.search({
        page,
        limit: 50,
        search: search || undefined,
        includeSystem: true,
        includeCustom: true,
      });

      return {
        items: result.items.map((f) => ({
          id: `library:${f.id}`,
          label: `${f.manufacturer?.name} - ${f.name} (${f.colorName})`,
        })),
        hasMore: result.pagination.page < result.pagination.totalPages,
      };
    },
    []
  );

  const sections: ComboBoxSection[] = [
    {
      title: 'My Filaments',
      fetchOptions: fetchTenantFilaments,
      emptyMessage: 'No filaments in your library...',
    },
    {
      title: 'Filament Library',
      fetchOptions: fetchLibraryFilaments,
    },
  ];

  const handleChange = (selectedValue: string) => {
    const isFromLibrary = selectedValue.startsWith('library:');
    onChange(selectedValue, isFromLibrary);
  };

  return (
    <InfiniteComboBox
      value={value}
      onChange={handleChange}
      sections={sections}
      label={label}
      placeholder="Search filaments..."
      error={error}
      disabled={disabled}
      allowEmpty={false}
    />
  );
}
